import { useEffect, useState, useRef, createContext, useContext } from "react";
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import axios from "axios";

// Pages
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Dashboard from "@/pages/Dashboard";
import NewScan from "@/pages/NewScan";
import ScanResults from "@/pages/ScanResults";
import Compliance from "@/pages/Compliance";
import Reports from "@/pages/Reports";
import Settings from "@/pages/Settings";
import Monitoring from "@/pages/Monitoring";
import Billing from "@/pages/Billing";
import ThreatProtection from "@/pages/ThreatProtection";
import AIGovernance from "@/pages/AIGovernance";
import RiskAssessment from "@/pages/RiskAssessment";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

// Auth Provider Component
const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(() => localStorage.getItem('auth_token'));

  const checkAuth = async () => {
    const storedToken = localStorage.getItem('auth_token');
    if (!storedToken) {
      setLoading(false);
      return null;
    }

    try {
      const response = await axios.get(`${BACKEND_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${storedToken}` },
        withCredentials: true
      });
      setUser(response.data);
      setToken(storedToken);
      setLoading(false);
      return response.data;
    } catch (error) {
      setUser(null);
      setToken(null);
      localStorage.removeItem('auth_token');
      setLoading(false);
      return null;
    }
  };

  const login = (newToken, userData) => {
    setToken(newToken);
    setUser(userData);
    localStorage.setItem('auth_token', newToken);
  };

  const logout = async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/auth/logout`, {}, { 
        withCredentials: true,
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
    } catch (e) {
      console.error(e);
    }
    setToken(null);
    setUser(null);
    localStorage.removeItem('auth_token');
  };

  // Check auth on mount
  useEffect(() => {
    checkAuth();
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, token, checkAuth, login, logout, setUser }}>
      {children}
    </AuthContext.Provider>
  );
};

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const AuthCallback = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { login } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processSession = async () => {
      const hash = location.hash;
      const sessionId = new URLSearchParams(hash.slice(1)).get('session_id');
      
      if (!sessionId) {
        navigate('/login');
        return;
      }

      try {
        const response = await axios.post(`${BACKEND_URL}/api/auth/session`, {
          session_id: sessionId
        }, { withCredentials: true });
        
        login(response.data.session_token, response.data.user);
        navigate('/dashboard', { replace: true });
      } catch (error) {
        console.error('Auth callback error:', error);
        navigate('/login');
      }
    };

    processSession();
  }, []);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <div className="animate-pulse text-primary">Processing authentication...</div>
    </div>
  );
};

const ProtectedRoute = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, loading, checkAuth, token } = useAuth();
  const [checking, setChecking] = useState(true);
  const hasChecked = useRef(false);

  useEffect(() => {
    if (hasChecked.current) return;
    hasChecked.current = true;

    const verify = async () => {
      // If we already have a user, we're good
      if (user) {
        setChecking(false);
        return;
      }

      // Check token from localStorage
      const storedToken = localStorage.getItem('auth_token');
      if (!storedToken) {
        navigate('/login', { replace: true });
        return;
      }

      // Verify token with backend
      const userData = await checkAuth();
      if (!userData) {
        navigate('/login', { replace: true });
      }
      setChecking(false);
    };

    verify();
  }, []);

  if (loading || checking) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-pulse text-primary">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  return children;
};

function AppRouter() {
  const location = useLocation();
  const { user, logout, token } = useAuth();

  // Check URL fragment for session_id (sync check before render)
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route 
        path="/dashboard" 
        element={
          <ProtectedRoute>
            <Dashboard user={user} logout={logout} token={token} />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/scan/new" 
        element={
          <ProtectedRoute>
            <NewScan user={user} logout={logout} token={token} />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/scan/:scanId" 
        element={
          <ProtectedRoute>
            <ScanResults user={user} logout={logout} token={token} />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/scans" 
        element={
          <ProtectedRoute>
            <Dashboard user={user} logout={logout} token={token} />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/compliance" 
        element={
          <ProtectedRoute>
            <Compliance user={user} logout={logout} token={token} />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/reports" 
        element={
          <ProtectedRoute>
            <Reports user={user} logout={logout} token={token} />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/monitoring" 
        element={
          <ProtectedRoute>
            <Monitoring user={user} logout={logout} token={token} />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/settings" 
        element={
          <ProtectedRoute>
            <Settings user={user} logout={logout} token={token} />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/billing" 
        element={
          <ProtectedRoute>
            <Billing user={user} logout={logout} token={token} />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/threat-protection" 
        element={
          <ProtectedRoute>
            <ThreatProtection user={user} logout={logout} token={token} />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/ai-governance" 
        element={
          <ProtectedRoute>
            <AIGovernance user={user} logout={logout} token={token} />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/risk-assessment" 
        element={<RiskAssessment />}
      />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRouter />
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
